import { useState } from 'react'
import { format } from 'date-fns'
import { LogOut } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useLogout, useSession } from '@/hooks/useAuth'
import { displayName } from '@/api/auth'

const GLOW = 'hover:shadow-[0_0_6px_rgba(16,185,129,0.8),0_0_14px_rgba(16,185,129,0.45)] focus-visible:shadow-[0_0_6px_rgba(16,185,129,0.8),0_0_14px_rgba(16,185,129,0.45)] data-[state=open]:shadow-[0_0_6px_rgba(16,185,129,0.8),0_0_14px_rgba(16,185,129,0.45)]'

/** Header chip showing who's signed in ("• Karl · Sep 25"); opens a sign-out popover. */
export function AccountChip() {
  const { data: session } = useSession()
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  if (!session?.authenticated) return null

  const name = displayName(session)
  const since = session.signedInAt ? new Date(session.signedInAt) : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-emerald-500 font-mono text-xs text-emerald-700 dark:text-emerald-400 transition-shadow focus-visible:outline-none ${GLOW}`}
          aria-label={`Signed in as ${session.email ?? name}. Account menu`}
        >
          <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
          <span>{name}</span>
          {since && <span className="hidden md:inline text-muted-foreground/70">· {format(since, 'MMM d')}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <p className="text-sm font-medium">{name}</p>
        {session.email && <p className="text-xs text-muted-foreground truncate">{session.email}</p>}
        {since && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Signed in {format(since, 'MMM d, yyyy')} on this device
          </p>
        )}
        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="mt-3 w-full flex items-center justify-center gap-2 h-8 rounded-md border border-border text-sm hover:bg-muted transition-colors disabled:opacity-60"
        >
          <LogOut size={14} aria-hidden />
          {logout.isPending ? 'Signing out…' : 'Sign out'}
        </button>
      </PopoverContent>
    </Popover>
  )
}
